import { createMongoAbility } from "@casl/ability";
import { useAbility } from "@casl/react";
import axios from "axios";
import { decode } from "jwt-simple";

import { AbilityContext } from "../contexts/AbilityContext";

// Singleton ability instance to update dynamically
export const ability = createMongoAbility();

// True once the first /user/capabilities response (or its failure) has been applied, so
// gates can tell "not allowed" from "not loaded yet" before redirecting.
let capabilitiesLoaded = false;

export const updateAbility = (rules: any[]) => {
  capabilitiesLoaded = true;
  ability.update(rules);
};

let fetchCounter = 0;

/**
 * 12-byte MongoDB ObjectId serialized as hex — avoids treating static /projects/<slug> routes as project ids.
 *
 * Caution: if project URLs ever use non-ObjectId identifiers (UUIDs, slugs as canonical id, etc.), path inference
 * will not pick them up; call fetchCapabilities(explicitId) from the route layer or widen this validator deliberately.
 */
const MONGO_OBJECT_ID_HEX = /^[a-fA-F0-9]{24}$/;

function projectIdFromPathname(pathname: string): string | undefined {
  const match = pathname.match(/\/projects\/([^/]+)/);
  const segment = match?.[1];
  return segment && MONGO_OBJECT_ID_HEX.test(segment) ? segment : undefined;
}

export const fetchCapabilities = async (projectId?: string) => {
  fetchCounter += 1;
  const currentFetchId = fetchCounter;

  const token = axios.defaults.headers.common.authorization;
  // Not logged in — no token means /user/capabilities would just 403. Reset to
  // no permissions and skip the request.
  if (!token) {
    updateAbility([]);
    return;
  }
  if (token) {
    try {
      const parsedToken = decode(token as string, null, true);
      if (parsedToken && parsedToken.sub && parsedToken.sub.startsWith("visitor:")) {
        const capabilities = parsedToken["https://theirstory.io/token-capabilities"];
        if (capabilities && capabilities[0]) {
          const cap = capabilities[0];
          const storyId = cap.story.replace(/^(edit_transcript|read_transcript):/, "");
          if (cap.type === "EditTranscript") {
            updateAbility([
              { action: "read", subject: "Story", conditions: { id: storyId } },
              { action: "update", subject: "Story", conditions: { id: storyId } },
            ]);
          } else if (cap.type === "ReadTranscript") {
            updateAbility([{ action: "read", subject: "Story", conditions: { id: storyId } }]);
          }
        }
        return;
      }
    } catch (e) {
      // ignore
    }
  }

  try {
    let effectiveProjectId: string | undefined;
    if (projectId != null && projectId !== "") {
      effectiveProjectId = MONGO_OBJECT_ID_HEX.test(projectId) ? projectId : undefined;
    } else {
      effectiveProjectId = projectIdFromPathname(window.location.pathname);
    }

    const url = effectiveProjectId ? `/user/capabilities?projectId=${effectiveProjectId}` : "/user/capabilities";
    const response = await axios.get(url);

    if (currentFetchId === fetchCounter) {
      if (response.data && response.data.rules) {
        updateAbility(response.data.rules);
      }
    }
  } catch (error) {
    if (currentFetchId === fetchCounter) {
      console.error("Failed to fetch capabilities", error);
      updateAbility([]);
    }
  }
};

export const usePermissions = () => useAbility(AbilityContext);

/** Re-renders on ability updates (via useAbility) and reports whether capabilities have loaded. */
export const useCapabilitiesLoaded = () => {
  usePermissions();
  return capabilitiesLoaded;
};

/**
 * Canonical derived role flags — import these instead of re-deriving per-component.
 *
 * Derivation rules (CASL-only, no raw role strings):
 *   isAdmin        → ability.can("manage", "all") — only system admin has this
 *   isOrgManager   → can create/delete Organization (granted to org managers + admins)
 *   isProjectManager → can update Project
 *   isMember       → can create Story
 *   isGuest        → no permissions (only when auth state is guest)
 *   isViewOnly     → none of the above (read-only access)
 */
export const useRoleFlags = () => {
  const currentAbility = usePermissions();

  const token = axios.defaults.headers.common.authorization;
  let isVisitor = false;
  let isVisitorReadOnly = false;
  if (token) {
    try {
      const parsedToken = decode(token as string, null, true);
      if (parsedToken && parsedToken.sub && parsedToken.sub.startsWith("visitor:")) {
        isVisitor = true;
        isVisitorReadOnly = parsedToken["https://theirstory.io/token-capabilities"]?.[0]?.type === "ReadTranscript";
      }
    } catch (e) {}
  }

  const isAdmin = currentAbility.can("manage", "all");
  const isOrgManager = currentAbility.can("create", "Organization") || currentAbility.can("delete", "Organization");
  const isProjectManager = currentAbility.can("update", "Project");
  const isMember = currentAbility.can("create", "Story");
  const isGuest =
    isVisitor || (!isAdmin && !isOrgManager && !isProjectManager && !isMember && currentAbility.rules.length === 0);
  const isViewOnly = isVisitor
    ? isVisitorReadOnly
    : !isAdmin && !isOrgManager && !isProjectManager && !isMember && !isGuest;
  return { ability: currentAbility, isAdmin, isOrgManager, isProjectManager, isMember, isViewOnly, isGuest };
};
